import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown, User2, LogOut, ShieldCheck, Car } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();

  const navItems = [
    { to: "/", label: "Home" },
    { to: "/vehicles", label: "Browse" },
    { to: "/drive-alfa-club", label: "Drive Alfa Club" },
    { to: "/alfa-one-path", label: "Business (Alfa One Path)" },
    { to: "/about", label: "About & How it works" },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur" data-testid="site-header">
      <div className="container-x flex h-16 items-center justify-between">
        <Logo variant="wordmark" height={28} to="/" />

        <nav className="hidden lg:flex items-center gap-0.5">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `text-sm px-3 py-2 rounded-lg transition-colors duration-150 ${
                  isActive
                    ? "text-foreground bg-[hsl(var(--blue-100))]"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`
              }
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {!user && (
            <>
              <Button variant="ghost" asChild data-testid="header-login-button" className="rounded-xl">
                <Link to="/login">Sign in</Link>
              </Button>
              <Button asChild className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" data-testid="header-reserve-button">
                <Link to="/vehicles">Browse vehicles</Link>
              </Button>
            </>
          )}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-xl" data-testid="header-user-menu">
                  <User2 className="h-4 w-4 mr-2" />
                  {user.full_name?.split(" ")[0] || "Account"}
                  <ChevronDown className="h-3.5 w-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                  {user.role === "admin" ? "Admin" : "Customer"}
                </DropdownMenuLabel>
                {user.role === "admin" ? (
                  <DropdownMenuItem asChild data-testid="user-menu-admin">
                    <Link to="/admin"><ShieldCheck className="h-4 w-4 mr-2" /> Admin Portal</Link>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem asChild data-testid="user-menu-portal">
                    <Link to="/portal"><Car className="h-4 w-4 mr-2" /> My Portal</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="user-menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <button
          className="lg:hidden p-2 rounded-lg border border-border"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-menu">
          <div className="container-x py-3 flex flex-col gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm ${isActive ? "bg-[hsl(var(--blue-100))]" : "hover:bg-secondary"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex flex-col gap-2 pt-2">
              {!user ? (
                <>
                  <Button variant="outline" asChild className="rounded-xl w-full">
                    <Link to="/login" onClick={() => setOpen(false)}>Sign in</Link>
                  </Button>
                  <Button asChild className="rounded-xl w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]">
                    <Link to="/vehicles" onClick={() => setOpen(false)}>Browse vehicles</Link>
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" asChild className="rounded-xl w-full">
                    <Link to={user.role === "admin" ? "/admin" : "/portal"} onClick={() => setOpen(false)}>
                      {user.role === "admin" ? "Admin Portal" : "My Portal"}
                    </Link>
                  </Button>
                  <Button variant="ghost" className="rounded-xl w-full" onClick={() => { logout(); setOpen(false); }}>Sign out</Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
